import React, { createContext, useContext, ReactNode, useState, useMemo, useCallback } from 'react';
import { useBusinessOperations, GoCampaignObject, GoCampaignSectionKey } from '@/context/schema/useBusinessOperations';
import { useFirebase } from '@/context/FirebaseProvider';
import { Business } from '@/schemas/business';

// Define SectionType for clarity
export type SectionType = 'services' | 'location' | 'differentiators' | 'promotions' | 'leadDelivery';

interface GoCampaignContextType {
  isLoading: boolean;
  data: GoCampaignObject;
  saveSectionData: <K extends GoCampaignSectionKey>(businessId: string, sectionKey: K, sectionData: GoCampaignObject[K]) => Promise<boolean>;
  
  // Track completed sections
  completedSections: {
    services?: boolean;
    locations?: boolean;
    differentiators?: boolean;
    promotions?: boolean;
    leadDelivery?: boolean;
  };
  
  // UI state for section components
  openSections: {
    services?: boolean;
    locations?: boolean;
    differentiators?: boolean;
    promotions?: boolean;
    leadDelivery?: boolean;
  };
  activeStickyHeader: string | null;
  hasSaved: {
    services?: boolean;
    locations?: boolean;
    differentiators?: boolean;
    promotions?: boolean;
    leadDelivery?: boolean;
  };
  hasUnsavedChanges: {
    services?: boolean;
    locations?: boolean;
    differentiators?: boolean;
    promotions?: boolean;
    leadDelivery?: boolean;
  };
  showCancelButtons: {
    services?: boolean;
    locations?: boolean;
    differentiators?: boolean;
    promotions?: boolean;
    leadDelivery?: boolean;
  };
  currentSection: SectionType | null;
  setCurrentSection: (sectionType: SectionType | null) => void;
  moveToNextSection: (currentSectionType: SectionType) => void;
  firstIncompleteSectionIndex: number | null;
  SECTION_ORDER: SectionType[];
}

// Create a default context
const GoCampaignContext = createContext<GoCampaignContextType>({
  isLoading: false,
  data: {},
  saveSectionData: async (_businessId: string, _sectionKey, _sectionData) => false,
  completedSections: {},
  openSections: {},
  activeStickyHeader: null,
  hasSaved: {},
  hasUnsavedChanges: {},
  showCancelButtons: {},
  currentSection: 'services',
  setCurrentSection: () => {},
  moveToNextSection: () => {},
  firstIncompleteSectionIndex: 0,
  SECTION_ORDER: ['services', 'location', 'differentiators', 'promotions', 'leadDelivery'],
});

// Hook for using the context
export const useGoCampaign = () => useContext(GoCampaignContext);

interface GoCampaignProviderProps {
  children: ReactNode;
  businessId: string;
}

export const GoCampaignProvider: React.FC<GoCampaignProviderProps> = ({
  children,
  businessId
}) => {
  const { userData, businesses } = useFirebase();
  const business = useMemo(() => businesses.find(b => b.id === businessId), [businesses, businessId]);
  
  const { saveGoCampaignSection, isLoading } = useBusinessOperations(userData);

  const [currentSection, setCurrentSection] = useState<SectionType | null>('services');

  // Data is sourced from the business object
  const data: GoCampaignObject = useMemo(() => business?.gocampaign || {}, [business?.gocampaign]);
  
  // Determine section completion status based on section data
  const completedSections = useMemo(() => ({
    services: !!data.services,
    locations: !!data.locations,
    differentiators: !!data.differentiators,
    promotions: !!data.promotions,
    leadDelivery: !!data.leadDelivery
  }), [data]); 
  
  // Section order for sequential flow
  const sectionOrder: SectionType[] = ['services', 'location', 'differentiators', 'promotions', 'leadDelivery'];

  // Find the first incomplete section
  const firstIncompleteSectionIndex = useMemo(() => {
    if (!business || business.status !== 'onboarding') {
      return null;
    }
    
    for (let i = 0; i < sectionOrder.length; i++) {
      const section = sectionOrder[i] as keyof typeof completedSections;
      if (!completedSections[section]) {
        return i;
      }
    }
    
    return null; // All sections are complete
  }, [business, completedSections, sectionOrder]);

  // Move to the next section in the sequence
  const moveToNextSection = useCallback((completedSectionType: SectionType) => {
    const currentIndex = sectionOrder.indexOf(completedSectionType);
    if (currentIndex >= 0 && currentIndex < sectionOrder.length - 1) {
      const nextSection = sectionOrder[currentIndex + 1];
      setCurrentSection(nextSection);
    } else if (currentIndex === sectionOrder.length - 1) {
      // Last section completed
      setCurrentSection(null);
    }
  }, [sectionOrder]);
  
  // Derive open sections from current section
  const openSections = useMemo(() => {
    const newOpenSections: Record<string, boolean> = {};
    if (currentSection) {
      newOpenSections[currentSection] = true;
    }
    return newOpenSections;
  }, [currentSection]);

  // Derive saved status from completed sections
  const hasSaved = useMemo(() => ({
    services: completedSections.services,
    locations: completedSections.locations,
    differentiators: completedSections.differentiators,
    promotions: completedSections.promotions,
    leadDelivery: completedSections.leadDelivery
  }), [completedSections]);
  
  // Default values for unsaved changes and cancel buttons
  const hasUnsavedChanges = {
    services: false,
    locations: false,
    differentiators: false,
    promotions: false,
    leadDelivery: false
  };
  
  const showCancelButtons = {
    services: false,
    locations: false,
    differentiators: false,
    promotions: false,
    leadDelivery: false
  };
  
  // Memoized handler for setting current section
  const handleSetCurrentSection = useCallback((section: SectionType | null) => {
    setCurrentSection(section);
  }, []);
  
  // Create the context value with required properties
  const contextValue = useMemo<GoCampaignContextType>(() => ({
    isLoading,
    data,
    saveSectionData: (currentBusinessId, sectionKey, sectionData) => 
      saveGoCampaignSection(currentBusinessId, sectionKey, sectionData),
    completedSections,
    openSections,
    activeStickyHeader: null,
    hasSaved,
    hasUnsavedChanges,
    showCancelButtons,
    currentSection,
    setCurrentSection: handleSetCurrentSection,
    moveToNextSection,
    firstIncompleteSectionIndex,
    SECTION_ORDER: sectionOrder,
  }), [
    isLoading,
    data,
    saveGoCampaignSection,
    completedSections,
    openSections,
    hasSaved,
    currentSection,
    handleSetCurrentSection,
    moveToNextSection,
    firstIncompleteSectionIndex,
    sectionOrder
  ]);
  
  return (
    <GoCampaignContext.Provider value={contextValue}>
      {children}
    </GoCampaignContext.Provider>
  );
};
